import { DebtLevels } from '@models';
import {
  addPaymentCardModalKey,
  checkoutSmartPromoModalShownKey,
  debtLevelCacheKey,
  debtLevelCacheLifetime,
  isSellerKey,
  messageToSubscribersModalShownKey,
  navProfileCacheLifetime,
  navProfileKey,
} from '@constants';
import { useSessionState } from '@utils/storage';

type DebtLevel = {
  viewer: {
    balance: number,
    debtLevel?: DebtLevels | null | undefined
  },
};
type CachedDebtLevel = DebtLevel & { validUntil: number };

export const getDebtLevelFromCache = (): DebtLevel | null | undefined => {
  const now = new Date().getTime();
  const cachedData = JSON.parse(sessionStorage.getItem(debtLevelCacheKey) || '{}') as CachedDebtLevel;

  if (cachedData && now < cachedData.validUntil) {
    return cachedData;
  }

  return;  
};

export const cacheDebtLevel = (data: DebtLevel) => {
  const now = new Date().getTime();
  const dataToCache: CachedDebtLevel = {
    ...data,
    validUntil: now + debtLevelCacheLifetime,
  };
  sessionStorage.setItem(
    debtLevelCacheKey,
    JSON.stringify(dataToCache),
  );
};

type UseCachedStateOptions = {
  timeout: number
  cacheKey: string
};
type UseCachedStateReturnValue<T> = [T | undefined, (data: T | undefined) => void];
type CachedData<T> = { data: T, validUntil: number };

function useCachedState<T>(options: UseCachedStateOptions): UseCachedStateReturnValue<T> {
  const now = new Date().getTime();
  const { timeout, cacheKey } = options;
  const [persistedState, setPersistedState] = useSessionState<CachedData<T>>(cacheKey);

  const validUntil = persistedState?.validUntil;
  const cachedState = validUntil && validUntil > now ? persistedState?.data : undefined;
  const setCachedState = (value: T) => {
    setPersistedState({
      data: value,
      validUntil: now + timeout,
    });
  };

  return [cachedState, setCachedState];
}

type NavProfileInfo = {
  viewer?: {
    id: number
    username: string
    avatar: string | undefined | null
    unreadMessagesCount: number
    hasUnreadNotifications?: boolean | null
    isSeller: boolean
  }
  basket?: {
    productsCount: number
  }
};
export const useCachedNavProfile = () => (
  useCachedState<NavProfileInfo>({
    cacheKey: navProfileKey,
    timeout: navProfileCacheLifetime,
  })
);

export const getIsSellerFromCache = () => sessionStorage.getItem(isSellerKey);
export const setIsSellerToCache = (isSeller: boolean) => (
  sessionStorage.setItem(isSellerKey, String(isSeller))
);

export const getIsAddPaymentCardShownFromCache = (userId: number) => {
  const data = localStorage.getItem(addPaymentCardModalKey);

  if (data) {
    const dataMap = JSON.parse(data) as Record<number, boolean>;
    return !!dataMap[userId];
  }

  return false;
};
export const setIsAddPaymentCardShownToCache = (userId: number) => {
  const data = localStorage.getItem(addPaymentCardModalKey);

  const dataMap = data ? JSON.parse(data) as Record<number, boolean> : {};

  localStorage.setItem(
    addPaymentCardModalKey,
    JSON.stringify({
      ...dataMap,
      [userId]: true,
    }),
  );
};

export const getIsMessageToSubscribersModalShownFromCache = () => (
  localStorage.getItem(messageToSubscribersModalShownKey)
);
export const setIsMessageToSubscribersModalShownToCache = () => {
  localStorage.setItem(messageToSubscribersModalShownKey, 'true');
};

export const getIsCheckoutSmartPromoModalShownFromCache = () => (
  sessionStorage.getItem(checkoutSmartPromoModalShownKey)
);
export const setIsCheckoutSmartPromoModalShownToCache = () => {
  sessionStorage.setItem(checkoutSmartPromoModalShownKey, 'true');
};
